import { useState } from 'react';
import { TrendingUp, Banknote, Fuel as FuelIcon, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useForecast } from '@/api/hooks/useForecast';
import { ForecastChart } from '@/components/forecasting/ForecastChart';
import type { ForecastMetric, ForecastResponse } from '@/types/forecast';

const METRICS: { key: ForecastMetric; label: string }[] = [
  { key: 'spend_zar', label: 'Spend (ZAR)' },
  { key: 'litres', label: 'Volume (litres)' },
];

const ZAR0 = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0,
});
const NUM0 = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });

function formatValue(v: number | null | undefined, unit: string): string {
  if (v == null) return '—';
  return unit === 'ZAR' ? ZAR0.format(v) : `${NUM0.format(v)} L`;
}

function formatMonth(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
}

export default function Forecasting() {
  const [metric, setMetric] = useState<ForecastMetric>('spend_zar');
  const { data, isLoading, error } = useForecast(metric, 36);
  const ciPct = Math.round((data?.ci_level ?? 0.95) * 100);

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Forecasting</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Budget and litre forecasting — monthly fleet{' '}
            {metric === 'spend_zar' ? 'spend' : 'consumption'} projected out to the next
            quarter, with a {ciPct}% confidence band.
          </p>
        </div>
        <MetricToggle metric={metric} onChange={setMetric} />
      </div>

      <SummaryCards data={data} isLoading={isLoading} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
          <CardTitle className="text-sm">
            {data?.label ?? 'Fleet'} — history &amp; {data?.summary.horizon_months ?? 3}-month
            forecast
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {data?.model_label ? `Model: ${data.model_label}` : 'Awaiting first model run'}
          </span>
        </CardHeader>
        <CardContent className="h-[360px] px-2 py-2">
          <ForecastChart data={data} isLoading={isLoading} error={(error as Error) ?? null} />
        </CardContent>
      </Card>

      <FooterNote data={data} />
    </div>
  );
}

function MetricToggle({
  metric,
  onChange,
}: {
  metric: ForecastMetric;
  onChange: (m: ForecastMetric) => void;
}): JSX.Element {
  return (
    <div className="inline-flex shrink-0 rounded-md border border-border p-0.5">
      {METRICS.map((m) => (
        <button
          key={m.key}
          type="button"
          onClick={() => onChange(m.key)}
          className={cn(
            'rounded px-3 py-1 text-xs font-medium transition-colors',
            metric === m.key
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function SummaryCards({
  data,
  isLoading,
}: {
  data: ForecastResponse | undefined;
  isLoading: boolean;
}): JSX.Element {
  const unit = data?.unit ?? 'ZAR';
  const s = data?.summary;
  const cards = [
    {
      icon: TrendingUp,
      title: 'Next month',
      value: formatValue(s?.next_month?.yhat, unit),
      subLabel: s?.next_month ? formatMonth(s.next_month.month) : '',
      sub: s?.next_month
        ? `${formatValue(s.next_month.yhat_lower, unit)} – ${formatValue(s.next_month.yhat_upper, unit)}`
        : 'awaiting forecast',
    },
    {
      icon: data?.metric === 'litres' ? FuelIcon : Banknote,
      title: `Next ${s?.horizon_months ?? 3} months (total)`,
      value: formatValue(s?.horizon_total_yhat, unit),
      subLabel: 'projected quarter',
      sub:
        s?.horizon_total_yhat != null
          ? `${formatValue(s.horizon_total_lower, unit)} – ${formatValue(s.horizon_total_upper, unit)}`
          : 'awaiting forecast',
    },
    {
      icon: Info,
      title: 'Last complete month',
      value: formatValue(s?.last_actual_value, unit),
      subLabel: 'actual',
      sub: formatMonth(s?.last_actual_month),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {cards.map((c) => (
        <Card key={c.title}>
          <CardHeader className="flex flex-row items-center gap-2 pb-1">
            <c.icon className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-xs font-medium text-muted-foreground">{c.title}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <>
                <div className="text-2xl font-semibold tracking-tight">{c.value}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {c.subLabel ? <span className="font-medium">{c.subLabel}</span> : null}
                  {c.subLabel && c.sub ? ' · ' : ''}
                  {c.sub}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function FooterNote({ data }: { data: ForecastResponse | undefined }): JSX.Element | null {
  if (!data) return null;

  if (!data.forecast.length) {
    return (
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0 text-amber-600" />
          No projection yet — the nightly forecaster (03:45 SAST) hasn't produced a model for
          this metric. The history above is live.
        </CardContent>
      </Card>
    );
  }

  const parts: string[] = [];
  if (data.model_label) parts.push(`Model: ${data.model_label}`);
  if (data.ci_level != null) parts.push(`${Math.round(data.ci_level * 100)}% confidence band`);
  if (data.data_anchor_month) parts.push(`trained through ${formatMonth(data.data_anchor_month)}`);
  if (data.generated_at) {
    parts.push(`generated ${new Date(data.generated_at).toLocaleString('en-ZA')}`);
  }
  return <p className="px-1 text-xs text-muted-foreground">{parts.join(' · ')}</p>;
}
